import React from 'react';
import styled from 'styled-components';
import { uniqueId } from 'lodash-es';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import countries from './assets/countries';

// @ts-ignore
import { gsap, TimelineMax, TweenMax, Power3, Power2, Power0, Linear } from 'gsap/all';

gsap.registerPlugin(TweenMax, TimelineMax, Power2, Power3, Power0, Linear);

//import './index.scss';

type CountryProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

const ModuleElementSVG = styled.svg<{ $styleText: string }>((props) => props.$styleText);

// https://codepen.io/osublake/pen/GRKLJrB/df06754f4736f45005fe693f3113049b

const Country = (componentProps: CountryProps) => {
  let props = componentProps;

  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);
  const [id] = React.useState(uniqueId('countrysvg-'));
  const [zoomData, setZoomData] = React.useState<any>(null);

  const contextValue = React.useMemo(() => {
    const viewPortWidth = props.properties.viewPortWidth ?? 2000;
    const viewPortHeight = props.properties.viewPortHeight ?? 1001;
    const width = props.properties.width ?? null;
    const height = props.properties.height ?? null;
    const selectedCountry = props.properties.selectedCountry ?? null;

    const sortedCountries = [...countries];

    /*
    sortedCountries.sort((a, b) => {
      if (a.id === selectedCountry) return -1;
      if (b.id === selectedCountry) return -1;
      return 0;
    });
    */

    const mapCountries: any[] = sortedCountries.map((country) => (
      <path
        key={country.id}
        id={`country-${country.id}`}
        className={`country country-${country.id}${selectedCountry === country.id ? ' selected' : ''}`}
        d={country.shape}
        vectorEffect="non-scaling-stroke"
      />
    ));

    return {
      viewPortWidth,
      viewPortHeight,
      width,
      height,
      mapCountries,
      selectedCountry,
    };
  }, [
    props.properties.viewPortWidth,
    props.properties.viewPortHeight,
    props.properties.width,
    props.properties.height,
    props.properties.selectedCountry,
  ]);

  React.useEffect(() => {
    const selectedCountry = props.properties.selectedCountry ?? null;

    if (selectedCountry) {
      const svgEl: SVGSVGElement | null = document.querySelector(`#svg-${id}`);
      const groupEl: SVGSVGElement | null = document.querySelector(`#${id}`);
      if (svgEl && groupEl) {
        const point = {
          x: 0,
          y: 0,
        };

        let [x, y] = [0, 0];

        let scaleFactor = 1 / 10;
        if (props.properties.scaleFactor && !isNaN(parseFloat(props.properties.scaleFactor))) {
          scaleFactor = 1 / parseFloat(props.properties.scaleFactor);
        }

        switch (selectedCountry) {
          case 'ROU':
            x = 1108.1;
            y = 255;
            break;
          case 'GBR':
            x = 950;
            y = 200;
            break;
          case 'CUW':
            x = 550;
            y = 494.9;
            break;
          case 'CAN':
            x = 350;
            y = 50;
            scaleFactor = 1 / 3;
            break;
          case 'COL':
            x = 500;
            y = 550;
            scaleFactor = 1 / 8;
            break;
          case 'BEL':
            x = 995;
            y = 230;
            scaleFactor = 1 / 12;
            break;
          case 'DNK':
            x = 1030;
            y = 190;
            scaleFactor = 1 / 12;
            break;
          case 'GRC':
            x = 1105;
            y = 315;
            scaleFactor = 1 / 12;
            break;
          case 'ITA':
            x = 1040;
            y = 290;
            scaleFactor = 1 / 10;
            break;
          case 'NLD':
            x = 1000;
            y = 220;
            scaleFactor = 1 / 14;
            break;
          case 'PRT':
            x = 930;
            y = 305;
            scaleFactor = 1 / 13;
            break;
          case 'ESP':
            x = 950;
            y = 305;
            scaleFactor = 1 / 10;
            break;
          case 'SWE':
            x = 1050;
            y = 140;
            scaleFactor = 1 / 8;
            break;
          case 'MEX':
            x = 300;
            y = 410;
            scaleFactor = 1 / 6;
            break;
          case 'INT':
          case 'EU':
            x = 0;
            y = 0;
            scaleFactor = 1;
            break;
        }

        setZoomData({
          x: -x * (scaleFactor - 1),
          y: -y * (scaleFactor - 1),
          width: 2000 * scaleFactor,
          height: 1001 * scaleFactor,
        });
      }
    } else {
      setZoomData(null);
    }
  }, [id, props.properties.selectedCountry, props.properties.scaleFactor]);

  React.useEffect(() => {
    let tl: any = null;

    if (zoomData) {
      tl = gsap.timeline({
        onCompleteParams: [],
        onComplete: () => {
          tl.kill();
        },
      });

      tl.to(`#svg-${id}`, {
        attr: { viewBox: `${zoomData.x} ${zoomData.y} ${zoomData.width} ${zoomData.height}` },
        duration: 0.6,
        ease: 'power1.inOut',
      });
    }

    return () => {
      tl?.kill?.();
    };
  }, [zoomData, id]);

  const svgProps: any = {};
  if (contextValue.width) svgProps.width = contextValue.width;
  if (contextValue.height) svgProps.height = contextValue.height;
  if (contextValue.viewPortWidth && contextValue.viewPortHeight) {
    svgProps.viewBox = `0 0 ${contextValue.viewPortWidth} ${contextValue.viewPortHeight}`;
  }

  return (
    <ModuleElementSVG
      className={`${componentProps.className ?? ''}${contextValue.selectedCountry ? ` country-${contextValue.selectedCountry}` : ''}`}
      $styleText={componentProps.styleText}
      id={`svg-${id}`}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 2000 2001"
      {...svgProps}
    >
      <g className="countries-wrapper" id={id}>
        {contextValue.mapCountries}
      </g>
    </ModuleElementSVG>
  );
};

export default Country;
